
import React from 'react';
import { SimulationConfig, BallStyle, EscapeColorMode, BallTexture } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { SETTING_INFO } from './settingInfo';
import { CollapsibleSection } from './CollapsibleSection';
import { VISUAL_PRESETS } from './presets';

import { motion } from 'motion/react';

interface VisualStylesSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const VisualStylesSection: React.FC<VisualStylesSectionProps> = ({ config, setConfig }) => {
  const ballStyleCategories = [
    { name: 'Sleek', styles: ['NONE', 'MATTE', 'GHOST', 'WIREFRAME'] },
    { name: 'Physical', styles: ['SHINY', 'NEON', 'CHROME', 'METAL', 'FROSTED'] },
    { name: 'Visual FX', styles: ['CRYSTAL', 'GLOW', 'HOLOGRAPHIC', 'PLASMA'] }
  ];
  
  const ballTextures: BallTexture[] = [
    'NONE', 'STRIPES', 'DOTS', 'CROSSHATCH', 'NOISE', 
    'CHECKERBOARD', 'HONEYCOMB', 'CIRCUIT', 'WAVES', 'STARS', 'BRICKS', 'SWIRL'
  ];
  const ringPatterns: EscapeColorMode[] = ['RAINBOW', 'ALTERNATING', 'GRADIENT', 'UNIFORM', 'INDIVIDUAL'];
  const isEscape = config.mode === 'ESCAPE';
  const escapeMsg = "This setting is designed specifically for ESCAPE mode.";

  const handleIndividualColorChange = (index: number, color: string) => {
    setConfig(p => {
        const current = Array.isArray(p.escapeIndividualColors) ? p.escapeIndividualColors : [];
        const newColors = [...current];
        newColors[index] = color;
        return { ...p, escapeIndividualColors: newColors };
    });
  };

  const ColorInput = ({ label, value, onChange, tooltip, settingKey, disabled, disabledMessage, modes }: { label: string, value: string, onChange: (v: string) => void, tooltip?: string, settingKey?: string, disabled?: boolean, disabledMessage?: string, modes?: string[] }) => (
    <ControlGroup label={label} tooltip={tooltip} settingKey={settingKey} disabled={disabled} disabledMessage={disabledMessage} modes={modes} darkMode={config.darkMode}>
        <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
            <div className="relative group">
                <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
                <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
            </div>
            <input 
                type="text" 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                className={`flex-1 px-3 py-2 text-[11px] font-mono font-bold border rounded-xl outline-none transition-all ${
                    config.darkMode 
                    ? 'bg-black/20 border-white/5 text-white focus:border-white/20' 
                    : 'bg-neutral-50 border-neutral-100 text-neutral-800 focus:border-neutral-300'
                }`} 
            />
        </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-2">
      <CollapsibleSection title="Visual Presets" icon="fa-magic" darkMode={config.darkMode} defaultOpen>
        <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50 border-purple-100'} mb-4 shadow-sm`}>
            <div className="grid grid-cols-2 gap-3">
                {VISUAL_PRESETS.map(p => (
                    <button
                        key={p.name}
                        onClick={() => setConfig(prev => ({ ...prev, ...p.settings }))}
                        className={`px-4 py-3 rounded-2xl text-[11px] font-bold transition-all h-14 flex items-center justify-center text-center ${
                            config.darkMode 
                            ? 'bg-white/5 hover:bg-white/10 text-white border border-white/5' 
                            : 'bg-white hover:bg-neutral-50 text-neutral-900 border border-neutral-200 shadow-sm'
                        }`}
                    >
                        {p.name}
                    </button>
                ))}
            </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Container & Borders" icon="fa-square" darkMode={config.darkMode} defaultOpen>
        <div className={`flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-yellow-500/5 border-yellow-500/10' : 'bg-yellow-50 border-yellow-100'} mb-4 shadow-sm`}>
            <SmartInput 
                label="Border Thickness" 
                min={1} max={50} 
                value={config.containerLineWidth || 4} 
                onChange={v => setConfig(p => ({...p, containerLineWidth: v}))} 
                darkMode={config.darkMode} 
                settingKey="containerLineWidth"
            />
            <ColorInput 
                label="Border Color" 
                value={config.borderColor || '#1a1a1a'} 
                onChange={v => setConfig(p => ({...p, borderColor: v}))} 
                settingKey="borderColor"
            />
            <ColorInput 
                label="Background Color" 
                value={config.outerColor} 
                onChange={v => setConfig(p => ({...p, outerColor: v}))} 
                settingKey="outerColor"
            />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Color & FX Studio" icon="fa-palette" darkMode={config.darkMode}>
        <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50 border-indigo-100'} mb-4 shadow-sm`}>
            <ControlGroup label="Master RGB Engine" settingKey="rgbLighting" darkMode={config.darkMode}>
                <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <Toggle active={config.rgbLighting} onClick={() => setConfig(p => ({...p, rgbLighting: !p.rgbLighting}))} color="bg-indigo-600" />
                </div>
            </ControlGroup>
            
            <div className="grid grid-cols-2 gap-3 mt-4">
                {[
                    { label: 'Ball RGB', key: 'useBallRGB' },
                    { label: 'FX RGB', key: 'useFXRGB' },
                    { label: 'Trail RGB', key: 'useTrailRGB' },
                    { label: 'Borders RGB', key: 'useContainerRGB' }
                ].map(({ label, key }) => (
                    <ControlGroup key={key} label={label} settingKey={key} darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <Toggle active={(config as any)[key]} onClick={() => setConfig(p => ({...p, [key]: !(p as any)[key]}))} />
                        </div>
                    </ControlGroup>
                ))}
            </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Sphere Studio" icon="fa-circle" darkMode={config.darkMode}>
        <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-zinc-500/5 border-white/5' : 'bg-zinc-50 border-neutral-100'} mb-4 shadow-sm flex flex-col gap-4`}>
            <div className="flex flex-col gap-4">
                {ballStyleCategories.map(cat => (
                    <ControlGroup key={cat.name} label={`${cat.name} Styles`} darkMode={config.darkMode}>
                        <div className={`grid grid-cols-4 gap-1.5 p-1.5 w-full rounded-2xl ${config.darkMode ? 'bg-black/30 border border-white/5' : 'bg-neutral-100'}`}>
                            {cat.styles.map(s => (
                                <button 
                                    key={s} 
                                    onClick={() => setConfig(prev => ({ ...prev, ballStyle: s as BallStyle }))} 
                                    className={`text-[9px] font-bold uppercase py-2 rounded-xl transition-all relative overflow-hidden ${config.ballStyle === s ? (config.darkMode ? 'text-white' : 'text-zinc-900 shadow-sm bg-white') : 'text-zinc-500'}`}
                                >
                                    {config.ballStyle === s && config.darkMode && (
                                        <motion.div layoutId={`activeBallStyle-${cat.name}`} className="absolute inset-0 z-0 bg-white/10" />
                                    )}
                                    <span className="relative z-10">{s}</span>
                                </button>
                            ))}
                        </div>
                    </ControlGroup>
                ))}
            </div>

            <ControlGroup label="Texture Overlay" settingKey="ballTexture" darkMode={config.darkMode}>
                <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <select 
                        value={config.ballTexture} 
                        onChange={e => setConfig(p => ({...p, ballTexture: e.target.value as BallTexture}))}
                        className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                    >
                        {ballTextures.map(t => <option key={t} value={t} className="bg-neutral-900 text-white">{t}</option>)}
                    </select>
                </div>
            </ControlGroup>

            <SmartInput 
                label="Texture Opacity" 
                min={0} max={1} step={0.1}
                value={config.ballTextureOpacity || 0.5} 
                onChange={v => setConfig(p => ({...p, ballTextureOpacity: v}))} 
                darkMode={config.darkMode} 
                settingKey="ballTextureOpacity"
            />

            <div className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                <ColorInput label="Primary Sphere" value={config.ball1Color} onChange={v => setConfig(p => ({...p, ball1Color: v}))} settingKey="ball1Color" />
                <ColorInput label="Secondary Sphere" value={config.ball2Color} onChange={v => setConfig(p => ({...p, ball2Color: v}))} settingKey="ball2Color" />
            </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Ring Pattern Studio" icon="fa-bullseye" darkMode={config.darkMode}>
        <div className={`flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-100'} mb-4 shadow-sm`}>
            <ControlGroup label="Pattern Mode" settingKey="escapeColorMode" disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} darkMode={config.darkMode}>
                <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <select value={config.escapeColorMode} onChange={e => setConfig(p => ({...p, escapeColorMode: e.target.value as EscapeColorMode}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                        {ringPatterns.map(m => <option key={m} value={m} className="bg-neutral-900 text-white">{m}</option>)}
                    </select>
                </div>
            </ControlGroup>
            
            {config.escapeColorMode === 'RAINBOW' && (
                <SmartInput 
                    label="Rainbow Concentration" 
                    min={0.1} max={50} step={0.1}
                    value={config.escapeRainbowSpeed || 1.0} 
                    onChange={v => setConfig(p => ({...p, escapeRainbowSpeed: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="escapeRainbowSpeed"
                    disabled={!isEscape}
                    disabledMessage={escapeMsg}
                    modes={['ESCAPE']}
                />
            )}
            
            {(config.escapeColorMode === 'ALTERNATING' || config.escapeColorMode === 'GRADIENT') && (
                <div className="grid grid-cols-1 gap-4">
                    <ColorInput label="Pattern Start" value={config.escapeColorA || '#ff0000'} onChange={v => setConfig(p => ({...p, escapeColorA: v}))} settingKey="escapeColorA" disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
                    <ColorInput label="Pattern End" value={config.escapeColorB || '#ffffff'} onChange={v => setConfig(p => ({...p, escapeColorB: v}))} settingKey="escapeColorB" disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
                </div>
            )}
            
            {config.escapeColorMode === 'UNIFORM' && (
                <ColorInput label="Ring Color" value={config.escapeUniformColor || '#3b82f6'} onChange={v => setConfig(p => ({...p, escapeUniformColor: v}))} settingKey="escapeUniformColor" disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} />
            )}
            
            {config.escapeColorMode === 'INDIVIDUAL' && (
                <ControlGroup label="Layer Colors" settingKey="escapeIndividualColors" disabled={!isEscape} disabledMessage={escapeMsg} modes={['ESCAPE']} darkMode={config.darkMode}>
                    <div className="flex flex-col gap-3 w-full">
                        <div className="grid grid-cols-5 gap-2.5">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="relative group">
                                    <div 
                                        className="w-full h-10 rounded-xl border-2 border-white/10 cursor-pointer shadow-lg transition-transform group-hover:scale-105" 
                                        style={{ backgroundColor: (Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6' }} 
                                    />
                                    <input 
                                        type="color" 
                                        value={(Array.isArray(config.escapeIndividualColors) && config.escapeIndividualColors[i]) || '#3b82f6'} 
                                        onChange={e => handleIndividualColorChange(i, e.target.value)}
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] font-bold opacity-30 italic">Colors loop infinitely for deeper layers.</p>
                    </div>
                </ControlGroup>
            )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Inner World Colors" icon="fa-vector-square" darkMode={config.darkMode}>
        <div className={`flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-zinc-500/5 border-white/5' : 'bg-zinc-50 border-neutral-100'} shadow-sm`}>
            <ColorInput label="Container Inner" value={config.innerColor} onChange={v => setConfig(p => ({...p, innerColor: v}))} settingKey="innerColor" />
            <ColorInput label="FX Waves" value={config.collisionWaveColor} onChange={v => setConfig(p => ({...p, collisionWaveColor: v}))} settingKey="collisionWaveColor" />
            <div className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                <ColorInput label="Blast Shards" value={config.blastColor} onChange={v => setConfig(p => ({...p, blastColor: v}))} settingKey="blastColor" />
                <ControlGroup label="Mirror Ring Color" settingKey="blastMatchRingColor" darkMode={config.darkMode}>
                    <div className={`flex items-center justify-end p-2 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                        <Toggle active={config.blastMatchRingColor} onClick={() => setConfig(p => ({...p, blastMatchRingColor: !p.blastMatchRingColor}))} />
                    </div>
                </ControlGroup>
            </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};
